// Layer 4 (UI): the current workbook-shaped FMEA row and its session diff.

import { useEffect, useRef } from 'react';
import { SurfacePagePanel } from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_SECTION_TITLE } from '@/shared/ui-class';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import {
  FMEA_PROPOSAL_SEAT,
  fmeaLearningModes,
  fmeaLearningRead,
  type FmeaCaseRead,
  type FmeaLearningCell,
  type FmeaLearningRead,
} from './FmeaLearning.transform';
import type { FailureMode } from '@/versions/v1/schema/case';
import { ActorChip } from './PipelineStrip';
import { GATE_APPROVED_SURFACE, GATE_PRIMARY_BUTTON, gatePillClass } from './gate-ui';

/**
 * One FMEA cell (comp `.fmea td`). A changed cell reads was → now at one size;
 * once the change is live the whole cell takes the `--accent-soft` wash (comp
 * `.applied .chg`), while a draft proposal (D19) reads the diff without it
 * (DESIGN.md § Crimson).
 *
 * K13c: `addition` draws the case's learned line under the cell's own text as
 * the comp's `.fmea .add` does — a green "+" line, never a replacement.
 */
function DiffCell({ cell, className, addition }: {
  cell: FmeaLearningCell;
  className?: string;
  addition?: { kind: 'cause' | 'warning'; text: string; added?: boolean };
}) {
  const live = cell.changed && cell.live !== false;
  return (
    <td
      data-fmea-changed={live ? 'true' : undefined}
      data-fmea-proposed={cell.changed && !live ? 'true' : undefined}
      className={cn(
        'border-b border-divider px-2 py-2 align-top transition-colors duration-500',
        live ? 'bg-[var(--accent-soft)]' : '',
        className,
      )}
    >
      {cell.changed && cell.previous ? (
        <>
          <del className="mr-1 text-muted">{cell.previous}</del>
          {/* K13d: one collapsible space carries the wrap opportunity. The cell is
              nowrap while the table has room (1470 keeps its one-line diff) and
              normal below 1000px, where the struck value wraps above the new one
              instead of colliding with the next cell (1280). */}
          {' '}
        </>
      ) : null}
      <span className={cell.changed ? 'font-semibold text-ink-primary' : ''}>{cell.value}</span>
      {addition ? <LearnedAddition addition={addition} /> : null}
    </td>
  );
}

/**
 * The comp's `.fmea .add` line: `+ <learned text>` in the addition colour.
 * K13c r2: `added: false` draws the live row's already-learned line as part of
 * the row — no "+", no addition colour — so an inherited lesson is never re-read
 * as new.
 */
function LearnedAddition({ addition }: { addition: { kind: 'cause' | 'warning'; text: string; added?: boolean } }) {
  const added = addition.added !== false;
  return (
    <>
      <br />
      <span
        data-fmea-learned={addition.kind}
        data-fmea-learned-added={added ? 'true' : 'false'}
        className={added ? 'font-semibold text-[var(--state-online)]' : 'text-ink-secondary'}
      >
        {added ? '+ ' : ''}{addition.text}
      </span>
    </>
  );
}

/** Column widths: the case page's diff row and the Learn list are both full width, so they share one layout. */
const FMEA_WIDTHS = ['w-[13%]', 'w-[21%]', 'w-[4%]', 'w-[4%]', 'w-[4%]', 'w-[6%]', 'w-[13%]', 'w-[24%]', 'w-[11%]'] as const;
const ROW_WIDTHS = { learn: FMEA_WIDTHS, case: FMEA_WIDTHS } as const;

const FMEA_TH = 'border-b border-divider px-2 pb-1.5 pt-0.5 text-xs font-semibold uppercase tracking-[0.07em] text-muted';

/**
 * K13d: the numeric cells keep a was → now pair on one line while the table has
 * room (1470 is unchanged); below 1000px the columns squeeze, so the pair wraps
 * at its collapsible space instead of running into the next cell (1280).
 */
const FMEA_NUMERIC = 'tabular-nums @[1000px]:whitespace-nowrap';

function FmeaRow({
  mode,
  state,
  variant = 'learn',
  read: readOverride,
}: {
  mode: FailureMode;
  state: FmeaLearningSessionState;
  variant?: keyof typeof ROW_WIDTHS;
  /** The case page's D19 read (draft, proposed or live); the Learn list reads the live row. */
  read?: FmeaLearningRead;
}) {
  const read = readOverride ?? fmeaLearningRead(mode, state);
  const [wMode, wCause, wS, wO, wD, wRpn, wSeen, wWarn, wEvidence] = ROW_WIDTHS[variant];
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto @container" data-fmea-table>
        <table className="w-full table-fixed border-collapse text-sm leading-snug">
          <thead>
            <tr>
              <th className={cn(FMEA_TH, wMode, 'text-left')}>Failure mode</th>
              <th className={cn(FMEA_TH, wCause, 'text-left')}>Cause</th>
              <th className={cn(FMEA_TH, wS, 'text-right')}>S</th>
              <th className={cn(FMEA_TH, wO, 'text-right')}>O</th>
              <th className={cn(FMEA_TH, wD, 'text-right')}>D</th>
              <th className={cn(FMEA_TH, wRpn, 'text-right')}>RPN</th>
              <th className={cn(FMEA_TH, wSeen, 'text-left')}>Last seen</th>
              <th className={cn(FMEA_TH, wWarn, 'text-left')}>Early warning</th>
              <th className={cn(FMEA_TH, wEvidence, 'text-left')}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            <tr className="text-ink-secondary">
              <td className="border-b border-divider px-2 py-2 align-top font-medium text-ink-primary">{read.failureMode}</td>
              <td className="border-b border-divider px-2 py-2 align-top">
                {read.cause}
                {read.learnedCause ? <LearnedAddition addition={{ kind: 'cause', text: read.learnedCause, added: read.learnedCauseAdded }} /> : null}
              </td>
              <DiffCell cell={read.severity} className={cn('text-right', FMEA_NUMERIC)} />
              <DiffCell cell={read.occurrence} className={cn('text-right', FMEA_NUMERIC)} />
              <DiffCell cell={read.detection} className={cn('text-right', FMEA_NUMERIC)} />
              <DiffCell cell={read.rpn} className={cn('text-right', FMEA_NUMERIC)} />
              <DiffCell cell={read.lastSeen} className={FMEA_NUMERIC} />
              <DiffCell
                cell={read.earlyWarning}
                addition={read.learnedEarlyWarning ? { kind: 'warning', text: read.learnedEarlyWarning, added: read.learnedEarlyWarningAdded } : undefined}
              />
              <DiffCell cell={read.evidence} className={FMEA_NUMERIC} />
            </tr>
          </tbody>
        </table>
      </div>
      {read.updatedLine && variant === 'learn' ? (
        <p className="text-xs text-muted" data-fmea-approval-record>
          {read.updatedLine}
        </p>
      ) : null}
    </div>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function FmeaGate({ read, onApprove }: { read: FmeaCaseRead; onApprove: () => void }) {
  if (read.gate === 'none') return null;
  const approved = read.gate === 'approved';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-xl border py-1 pl-2 pr-1 transition-colors duration-300',
        approved ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
      )}
      data-fmea-gate={read.gate}
    >
      <ActorChip actor="Human">{read.seatLabel}</ActorChip>
      {approved ? (
        <span className="px-2 text-xs font-semibold text-[var(--state-online)]">Approved</span>
      ) : read.gate === 'await-seat' ? (
        <span className="px-2 text-xs font-medium text-muted" data-fmea-gate-waiting>Awaiting approval</span>
      ) : (
        <button
          type="button"
          className={cn(GATE_PRIMARY_BUTTON, 'h-7 px-3')}
          disabled={read.gate !== 'approve'}
          title={read.gate === 'await-action' ? 'Opens once the action is approved' : undefined}
          onClick={onApprove}
        >
          Approve · {FMEA_PROPOSAL_SEAT}
        </button>
      )}
    </span>
  );
}

export function FmeaLearningPanel({
  mode,
  state,
  read,
  onApprove,
}: {
  mode: FailureMode;
  state: FmeaLearningSessionState;
  read: FmeaCaseRead;
  onApprove: () => void;
}) {
  const panelRef = useRef<HTMLElement | null>(null);
  const liveLine = read.phase === 'live' ? read.row.updatedLine : undefined;
  const shownLine = useRef(liveLine);

  // The second approval lights the diff in place: bring the row into view and mark the changed cells once.
  useEffect(() => {
    if (!liveLine || liveLine === shownLine.current) {
      shownLine.current = liveLine;
      return;
    }
    shownLine.current = liveLine;
    const panel = panelRef.current;
    if (!panel) return;
    const reduce = prefersReducedMotion();
    // Wait two frames so the stepper's approval growth has laid out before measuring the scroll.
    let second = 0;
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(() => {
        panel.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
        if (reduce) return;
        panel.querySelectorAll<HTMLElement>('[data-fmea-changed]').forEach((cell) => {
          cell.animate?.(
            [
              { boxShadow: 'inset 0 0 0 2px var(--color-accent-500)' },
              { boxShadow: 'inset 0 0 0 2px transparent' },
            ],
            { duration: 1400, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
          );
        });
      });
    });
    return () => {
      window.cancelAnimationFrame(first);
      window.cancelAnimationFrame(second);
    };
  }, [liveLine]);

  return (
    <section ref={panelRef} id="walkthrough-living-fmea" className="min-w-0 scroll-mb-4" data-fmea-learning-row={mode.id} data-fmea-phase={read.phase}>
      {/* K13d: the comp's card header — title left, the readers note right of it,
          gate far right (comp `.card h4` + `.cmp`). `SurfacePageSectionHeader`
          wraps its whole right group once a note joins the gate, which dropped
          the gate to a second line at 1470; this header keeps the gate on the
          title row and lets the title block absorb the squeeze instead. */}
      <div className="card-matte-2 flex min-w-0 flex-col">
        <div className="hairline-b flex items-baseline gap-x-4 px-4 pb-2.5 pt-3">
          <h2 className={cn(SURFACE_PAGE_SECTION_TITLE, 'min-w-0 flex-1')}>
            Living FMEA
            <span className="font-normal text-muted"> · </span>
            {mode.failureMode}
            {read.pill ? (
              <span
                className={cn(gatePillClass(read.pill.tone), 'ml-2.5 whitespace-nowrap align-[2px]')}
                data-fmea-pill={read.pill.tone}
                data-fmea-approval-record={read.pill.tone === 'approved' ? true : undefined}
              >
                {read.pill.text}
              </span>
            ) : null}
          </h2>
          {read.readers.note ? (
            <span className="shrink-0 whitespace-nowrap text-xs text-muted" data-fmea-readers>{read.readers.note}</span>
          ) : null}
          <FmeaGate read={read} onApprove={onApprove} />
        </div>
        <div className="min-h-0 flex-1 px-4 py-2.5">
          <FmeaRow mode={mode} state={state} variant="case" read={read.row} />
          {read.nextStep ? (
            <div className="mt-1.5 border-t border-divider pt-2 text-xs text-muted" data-fmea-next-step>{read.nextStep.lead} <span aria-hidden="true">→</span> <b className="font-semibold text-ink-secondary">{read.nextStep.rating}</b> {read.nextStep.suffix}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** Session Learn body mirrors the same engine row shown on the case page. */
export function FmeaLearningUpdates({ state, bare = false }: {
  state: FmeaLearningSessionState;
  /** Opt-in (polish.P28c): the rows without their panel, for a host card's tab. */
  bare?: boolean;
}) {
  const modes = fmeaLearningModes(state);
  if (modes.length === 0) return null;
  if (bare) {
    return (
      <div className="space-y-3 px-4 py-3">
        {modes.map((mode) => (
          <FmeaRow key={mode.id} mode={mode} state={state} />
        ))}
      </div>
    );
  }
  return (
    <SurfacePagePanel title="FMEA updates this session">
      <div className="space-y-3">
        {modes.map((mode) => (
          <FmeaRow key={mode.id} mode={mode} state={state} />
        ))}
      </div>
    </SurfacePagePanel>
  );
}
